import { fuseAnimations } from '@fuse/animations';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { map, take } from 'rxjs/operators';
import { StateService } from 'app/shared/services/state.service';
import { State } from 'app/shared/models/state';
import { Subscription } from 'rxjs';
import {
  enumReportType,
  Report,
  ReportOptions,
} from 'app/shared/models/report';
import { plural } from 'pluralize';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { ProcessTypeService } from 'app/shared/services/process-type.service';
import { Utilities } from 'app/shared/helpers/utilities';
import { IReportService } from 'app/shared/services/reporting/base-report.service';
import { ReportHelper } from 'app/shared/services/reporting/report-helper';
import { EventService } from 'app/shared/services/event.service';
import { UserService } from 'app/shared/services/user.service';
import { User } from 'app/shared/models/user';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-report-options',
  templateUrl: './report-options.component.html',
  styleUrls: ['./report-options.component.scss'],
  animations: fuseAnimations,
})
export class ReportOptionsComponent implements OnInit {
  optionsForm: UntypedFormGroup;
  filterList: any[] = [];
  subFilterList: any[] = [];
  organisationList: any[] = [];
  userList: any[] = [];
  stateList: State[] = [];
  fromDateFilter: string;
  toDateFilter: string;
  showSubFilter = false;
  showUserFilter = false;
  showTaskDateFilter = false;
  showReferralDateFilter = false;
  showOrganisationFilter = false;
  showIncludeArchive = true;
  showDateFilterOptions = true;
  showFilter = true;
  showStateFilter = true;
  showSingleSelectionFilter = false;
  includeRecordsNotLinkedToSubject = false;

  disableOptions:boolean = true;

  filterName = 'Filters';
  subFilterName = 'Filters';
  userFilterName = 'Users';
  organisationFilterName = 'Organisations';

  hasDateIncurred = false;
  dateIncurredLabel = 'Date Incurred';

  report: Report;

  hasChanges = true;
  hasData = false;

  @ViewChild('allSelected') private allSelected: MatOption;
  @ViewChild('allSubFiltersSelected') private allSubFiltersSelected: MatOption;
  @ViewChild('allUsersSelected') private allUsersSelected: MatOption;
  @ViewChild('allOrganisationsSelected')
  private allOrganisationsSelected: MatOption;

  reportChanged$: Subscription;
  reportDataChanged$: Subscription;
  reportService: IReportService;
  currentUser: User;
  currentUserOrganisationId: number;

  fromDatePlaceholder: string = 'From Date';
  toDatePlaceholder: string = 'To Date';

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private stateService: StateService,
    private subjectTypeService: SubjectTypeService,
    private processTypeService: ProcessTypeService,
    private reportHelper: ReportHelper,
    private eventService: EventService,
    private userService: UserService,
    private defaultDataService: DefaultDataService
  ) {
    this.optionsForm = this.fb.group(
      {
        stateFilter: [''],
        dateFilter: [''],
        reportTypeFilter: [''],
        dateFrom: null,
        dateTo: null,
        singleSelectionFilter: null,
        filters: this.filterList,
        subFilters: this.subFilterList,
        organisations: this.organisationList,
        users: this.userList,
        includeArchived: false,
      },
      { validator: this.validateDateInput }
    );
    this.currentUser = this.userService.getCurrentUser();

    // hide include archive flag if current user does not have can view archive ticked
    this.showIncludeArchive = this.currentUser.canViewArchive ? this.showIncludeArchive: false;

    this.currentUserOrganisationId =
      this.defaultDataService.limitAccessToSubjectsByOrg === true &&
      (!environment.currentUserIsMultiOrgAdmin) && 
      this.currentUser &&
      this.currentUser.organisationId
        ? this.currentUser.organisationId
        : undefined;
  }

  ngOnInit() {
    this.stateService
      .getAllData()
      .pipe(take(1))
      .subscribe((states) => {
        this.stateList = states
          .filter((x) => !x.hideRecord)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (environment.enableFOIFeature==false)
          this.stateList=this.stateList.filter(i=>i.uid!='c8318c86-f0e2-4c09-86eb-04d97270aaaa');  
      });

    var self = this;
    this.reportChanged$ = this.eventService.onReportItemChanged.subscribe(
      (report: Report) => {
        if (report) {
          this.reportService = this.reportHelper.getReportService(report);
          this.report = report;
          if (report.config) {
            report.config.filterId =
              report.config.filterId || report.config.groupBy;
          }

          let autoPop = true;

          if (
            report.config.reportType == enumReportType.ReportFindingsPerEmployee
          ) {
            self.fromDatePlaceholder = 'Referral From Date';
            self.toDatePlaceholder = 'Referral To Date';

            this.showIncludeArchive = false;
            this.showDateFilterOptions = false;
            this.showOrganisationFilter = true;
            this.showSubFilter = false;
            this.showFilter = false;
            this.showStateFilter = false;
            this.showSingleSelectionFilter = true;
            if (this.report.config.options === undefined) {
              this.report.config.options = new ReportOptions();
            }
            this.report.config.options.showUsers = false;
            this.report.config.options.includeArchived = false;
            this.report.config.options.organisationIncludeBlank = false;
            autoPop = false;
          }

          if (report.config.reportType == enumReportType.AnonymousFollowUp) {
            self.fromDatePlaceholder = 'Referral From Date';
            self.toDatePlaceholder = 'Referral To Date';

            this.showIncludeArchive = false;
            this.showDateFilterOptions = false;
            this.showOrganisationFilter = true;
            this.showSubFilter = false;
            this.showFilter = false;
            this.showStateFilter = false;
            if (this.report.config.options === undefined) {
              this.report.config.options = new ReportOptions();
            }
            this.report.config.options.showUsers = false;
            this.report.config.options.includeArchived = false;
            this.report.config.options.organisationIncludeBlank = false;
            autoPop = false;
          }
          if (report.config.reportType == enumReportType.IncidentvsReportDate) {
            self.fromDatePlaceholder = 'Case From Date';
            self.toDatePlaceholder = 'Case To Date';
            this.showIncludeArchive = false;
            this.showDateFilterOptions = false;
            this.showOrganisationFilter = true;
            this.showSubFilter = false;
            this.showFilter = false;
            this.showStateFilter = false;
            if (this.report.config.options === undefined) {
              this.report.config.options = new ReportOptions();
            }
            this.report.config.options.showUsers = false;
            this.report.config.options.includeArchived = false;
            this.report.config.options.organisationIncludeBlank = false;
            autoPop = false;
          }

          this.setOptions(this.report.config.options, autoPop);
        }
      }
    );

    this.reportDataChanged$ = this.eventService.onReportDataChanged.subscribe(
      (data) => {
        if (data && data?.reportData?.listData?.length>0) {
          this.hasData = JSON.parse(data.reportData.listData).length > 0;
          this.hasChanges = false;
          this.eventService.generateReportCompleted();
        }
      }
    );

    // patch checkboxes with relevant aria labelling
    setTimeout(() => {
      Utilities.patchCheckboxAria();
    }, 500);
  }

  setOptions(options?: ReportOptions, applyOptionsToReport: boolean = true) {
    const filterDataType =
      this.report.config.filterDataType || (this.report.config as any).dataType;
    if (this.report.config.filterLabel) {
      this.filterName = this.report.config.filterLabel;
    } else {
      this.reportService
        .getFilterName(filterDataType)
        .pipe(take(1))
        .subscribe((s) => {
          this.filterName = s;
        });
    }

    // only show/include Date Incurred if is included in process type
    if (this.report.config.processTypeId) {
      this.processTypeService
        .getById(this.report.config.processTypeId)
        .pipe(take(1))
        .subscribe((processType) => {
          const dataField = processType.dataFields.find(
            (i) => i.name.toLowerCase() === 'dateincurred'
          );
          if (dataField) {
            this.hasDateIncurred = true;
            this.dateIncurredLabel = dataField.label;
          }
        });
    } else {
      this.hasDateIncurred = false;
    }

    if (!this.showSingleSelectionFilter) {
      this.filterList = [];
      this.optionsForm.controls.filters.patchValue('');
      this.setFilter(filterDataType)
        .pipe(take(1))
        .subscribe((data) => {
          this.filterList = [...data];
          this.optionsForm.controls.filters.patchValue(
            options && options.filters && options.filters.length > 0
              ? this.getFilterStringAsArray(options.filters)
              : this.filterList.map((i) => i.filterValue)
          );
        });
    }

    if (this.showSingleSelectionFilter) {
      this.filterList = [];
      this.optionsForm.controls.singleSelectionFilter.patchValue('');
      this.setFilter(filterDataType, false)
        .pipe(take(1))
        .subscribe((data) => {
          this.filterList = [...data];
          this.optionsForm.controls.singleSelectionFilter.patchValue(
            options && options.filters && options.filters.length > 0
              ? Number(options.filters)
              : this.filterList[0].filterValue
          );
        });
    }

    if (this.report.config.subFilterId) {
      if (this.report.config.subFilterLabel) {
        this.subFilterName = this.report.config.subFilterLabel;
      } else {
        this.reportService
          .getFilterName(this.report.config.subFilterDataType)
          .pipe(take(1))
          .subscribe((s) => {
            this.subFilterName = s;
          });
      }

      this.subFilterList = [];
      this.optionsForm.controls.subFilters.patchValue('');
      this.setFilter(this.report.config.subFilterDataType)
        .pipe(take(1))
        .subscribe((data) => {
          this.subFilterList = [...data];
          this.optionsForm.controls.subFilters.patchValue(
            options && options.subFilters.length > 0
              ? this.getFilterStringAsArray(options.subFilters)
              : this.subFilterList.map((i) => i.filterValue)
          );
        });
    }
    this.showSubFilter = this.report.config.subFilterId ? true : false;

    // if report not already filtering based on organisation then build/show organisation filter
    this.organisationList = [];
    this.optionsForm.controls.organisations.patchValue('');
    const subjectTypeId = this.report.config.subjectTypeId || 1; // if no subject type id specified then use case (1)
    this.subjectTypeService
      .getById(subjectTypeId)
      .pipe(take(1))
      .subscribe((subjectType) => {
        const organisationField = subjectType
          ? subjectType.dataFields.find(
              (i) => i.dataType.toLowerCase() === 'organisation'
            )
          : null;
        const hasOrganisationField = organisationField ? true : false;
        this.showOrganisationFilter =
          (!this.currentUserOrganisationId) &&
          (this.report.config.organisationFilterId
            ? true
            : options
            ? options.showOrganisations === true
            : hasOrganisationField);

        if (this.showOrganisationFilter) {
          this.organisationFilterName =
            this.report.config.organisationFilterLabel ||
            (organisationField
              ? plural(organisationField.label)
              : 'Organisations');

          var includeBlank = true;
          if (options && options.organisationIncludeBlank != undefined)
            includeBlank = options.organisationIncludeBlank;
          this.setFilter('organisation', includeBlank)
            .pipe(take(1))
            .subscribe((data) => {
              this.organisationList = [...data];
              this.optionsForm.controls.organisations.patchValue(
                options &&
                  options.organisations &&
                  options.organisations.length > 0
                  ? this.getFilterStringAsArray(options.organisations)
                  : this.organisationList.map((i) => i.filterValue)
              );
            });
        }
      });

    // if report not already filtering based on user then build/show user filter
    this.userList = [];
    this.optionsForm.controls.users.patchValue('');
    const name = this.report.name.toLowerCase();
    this.showUserFilter = this.report.config.userFilterId ? true : false;
    if (!this.showUserFilter) {
      if (options) {
        this.showUserFilter = options.showUsers === true;
      } else {
        this.showUserFilter = !(
          name.includes('manager') ||
          name.includes('owner') ||
          name.includes('created by') ||
          name.includes('user')
        );
      }
    }

    //if task by task report, add date filters.
    this.showTaskDateFilter =
      this.report.config.filterDataType === 'tasktypes' ? true : false;

    this.showReferralDateFilter =
      this.report.config.reportType ===
        enumReportType.ConcernsByChannelSource ||
      this.report.config.reportType ===
        enumReportType.WhistleblowerReportSource ||
      this.report.config.reportType === enumReportType.SatisfactionOutcome ||
      this.report.config.reportType ===
        enumReportType.ConfidentialityBreakdown ||
      this.report.config.reportType === enumReportType.ConcernsByType ||
      this.report.config.reportType === enumReportType.ConcernsByStatus
        ? true
        : false;

    if (this.showUserFilter) {
      this.userFilterName = this.report.config.userFilterLabel || 'Users';
      this.setFilter('user')
        .pipe(take(1))
        .subscribe((data) => {
          this.userList = [...data];
          this.optionsForm.controls.users.patchValue(
            options && options.users.length > 0
              ? this.getFilterStringAsArray(options.users)
              : this.userList.map((i) => i.filterValue)
          );
        });
    }

    // set other controls
    if (options) {
      // update controls...
      this.fromDateFilter = options.dateFrom;
      this.toDateFilter = options.dateTo;

      this.optionsForm.controls.stateFilter.patchValue(options.state);
      this.optionsForm.controls.dateFilter.patchValue(options.dateType);
      this.optionsForm.controls.reportTypeFilter.patchValue(options.reportType);
      this.optionsForm.controls.dateFrom.patchValue(
        options.dateFrom && options.dateFrom.length > 0
          ? new Date(options.dateFrom.replace(/ /g, '/'))
          : ''
      );
      this.optionsForm.controls.dateTo.patchValue(
        options.dateTo && options.dateTo.length > 0
          ? new Date(options.dateTo.replace(/ /g, '/'))
          : ''
      );
      this.optionsForm.controls.includeArchived.patchValue(
        options.includeArchived
      );

      // ...get the data
      if (applyOptionsToReport === true) this.applyOptions();
    } else {
      this.optionsForm.controls.stateFilter.patchValue('');
      this.optionsForm.controls.dateFilter.patchValue('');
      this.optionsForm.controls.reportTypeFilter.patchValue('');
      this.optionsForm.controls.dateFrom.patchValue('');
      this.optionsForm.controls.dateTo.patchValue('');
      this.optionsForm.controls.includeArchived.patchValue(false);
    }
  }

  applyOptions() {
    this.hasChanges = false;
    const val = this.optionsForm.getRawValue();
    
    var filterData = null;
    if (this.showFilter) {
      filterData =
        val.filters && val.filters.length > 0
          ? val.filters.join()
          : this.filterList.length < 1
          ? ''
          : this.filterList.map((i) => i.filterValue).join();
    }
    if (this.showSingleSelectionFilter) {
      filterData = String(val.singleSelectionFilter);
    }

    var orgList = null;
    orgList = this.currentUserOrganisationId
      ? this.currentUserOrganisationId
      : val.organisations && val.organisations.length > 0
      ? val.organisations.join()
      : this.organisationList.length < 1
      ? ''
      : this.organisationList.map((i) => i.filterValue).join();

    if (this.report.config.options == undefined) {
      this.report.config.options = new ReportOptions();
    }
    var options: ReportOptions = this.report.config.options;

    options.state = val.stateFilter;
    options.dateType = val.dateFilter;
    options.reportType = val.reportTypeFilter;
    options.dateFrom = this.fromDateFilter;
    options.dateTo = this.toDateFilter;
    options.includeArchived = val.includeArchived;
    options.showUsers = this.showUserFilter === true;
    options.showOrganisations = this.showOrganisationFilter === true;
    options.filters = filterData;

    (options.subFilters =
      val.subFilters && val.subFilters.length > 0
        ? val.subFilters.join()
        : this.subFilterList.length < 1
        ? ''
        : this.subFilterList.map((i) => i.filterValue).join()),
      (options.users =
        val.users && val.users.length > 0
          ? val.users.join()
          : this.userList.length < 1
          ? ''
          : this.userList.map((i) => i.filterValue).join()),
      (options.organisations = orgList);

    // get the report data
    this.eventService.generateReportStarted();
    this.reportService.getReportData(this.report, options);
  }

  getFilterStringAsArray(s: string): any[] {
    if (s.length > 0) {
      const arr = s.split(',');
      const result: any[] = arr.filter((i) => i !== 'Blank').map((i) => +i);
      if (arr.filter((i) => i === 'Blank').length > 0) {
        result.push('Blank');
      }
      return result;
    }
    return [];
  }

  setFilter(dataType, addBlank: boolean = true) {
    let result = [];
    return this.reportService.getFilters(dataType).pipe(
      take(1),
      map((data) => {
        if (data) {
          result = [...data];
        }
        if (addBlank === true)
          result.unshift({ filterValue: `Blank`, filterName: 'Blank' });
        return result;
      })
    );
  }

  toggleFiltersAllSelection() {
    this.hasChanges = true;
    if (this.allSelected.selected) {
      this.optionsForm.controls.filters.patchValue([
        ...this.filterList.map((item) => item.filterValue),
        0,
      ]);
    } else {
      this.optionsForm.controls.filters.patchValue([]);
    }
  }

  toggleFiltersPerOne() {
    this.hasChanges = true;
    if (this.allSelected) {
      if (this.allSelected.selected) {
        this.allSelected.deselect();
        return false;
      }
      if (
        this.optionsForm.controls.filters.value.length ===
        this.filterList.length
      ) {
        this.allSelected.select();
      }
    }
  }

  openedChange(opened: boolean) {
    if (this.allSelected) {
      if (
        opened &&
        this.optionsForm.controls.filters.value &&
        this.filterList &&
        this.optionsForm.controls.filters.value.length ===
          this.filterList.length
      ) {
        this.allSelected.select();
      } else {
        this.allSelected.deselect();
      }
    }
  }

  toggleSubFiltersAllSelection() {
    this.hasChanges = true;
    if (this.allSubFiltersSelected.selected) {
      this.optionsForm.controls.subFilters.patchValue([
        ...this.subFilterList.map((item) => item.filterValue),
        0,
      ]);
    } else {
      this.optionsForm.controls.subFilters.patchValue([]);
    }
  }

  toggleSubFiltersPerOne() {
    this.hasChanges = true;
    if (this.allSubFiltersSelected) {
      if (this.allSubFiltersSelected.selected) {
        this.allSubFiltersSelected.deselect();
        return false;
      }
      if (
        this.optionsForm.controls.subFilters.value.length ===
        this.subFilterList.length
      ) {
        this.allSubFiltersSelected.select();
      }
    }
  }

  openedSubFilterChange(opened: boolean) {
    if (this.allSubFiltersSelected) {
      if (
        opened &&
        this.optionsForm.controls.subFilters.value &&
        this.subFilterList &&
        this.optionsForm.controls.subFilters.value.length ===
          this.subFilterList.length
      ) {
        this.allSubFiltersSelected.select();
      } else {
        this.allSubFiltersSelected.deselect();
      }
    }
  }

  toggleUsersAllSelection() {
    this.hasChanges = true;
    if (this.allUsersSelected.selected) {
      this.optionsForm.controls.users.patchValue([
        ...this.userList.map((item) => item.filterValue),
        0,
      ]);
    } else {
      this.optionsForm.controls.users.patchValue([]);
    }
  }

  toggleUsersPerOne() {
    this.hasChanges = true;
    if (this.allUsersSelected) {
      if (this.allUsersSelected.selected) {
        this.allUsersSelected.deselect();
        return false;
      }
      if (
        this.optionsForm.controls.users.value.length === this.userList.length
      ) {
        this.allUsersSelected.select();
      }
    }
  }

  openedUsersChange(opened: boolean) {
    if (this.allUsersSelected) {
      if (
        opened &&
        this.optionsForm.controls.users.value &&
        this.userList &&
        this.optionsForm.controls.users.value.length === this.userList.length
      ) {
        this.allUsersSelected.select();
      } else {
        this.allUsersSelected.deselect();
      }
    }
  }

  toggleOrganisationsAllSelection() {
    this.hasChanges = true;
    if (this.allOrganisationsSelected.selected) {
      this.optionsForm.controls.organisations.patchValue([
        ...this.organisationList.map((item) => item.filterValue),
        0,
      ]);
    } else {
      this.optionsForm.controls.organisations.patchValue([]);
    }
  }

  toggleOrganisationsPerOne() {
    this.hasChanges = true;
    if (this.allOrganisationsSelected) {
      if (this.allOrganisationsSelected.selected) {
        this.allOrganisationsSelected.deselect();
        return false;
      }
      if (
        this.optionsForm.controls.organisations.value.length ===
        this.organisationList.length
      ) {
        this.allOrganisationsSelected.select();
      }
    }
  }

  openedOrganisationsChange(opened: boolean) {
    if (this.allOrganisationsSelected) {
      if (
        opened &&
        this.optionsForm.controls.organisations.value &&
        this.organisationList &&
        this.optionsForm.controls.organisations.value.length ===
          this.organisationList.length
      ) {
        this.allOrganisationsSelected.select();
      } else {
        this.allOrganisationsSelected.deselect();
      }
    }
  }

  change(e) {
    this.hasChanges = true;
  }

  singleSelectionFilterChange(e) {
    this.hasChanges = true;
  }

  fromDateChange(e) {
    this.hasChanges = true;
    this.fromDateFilter = e ? ((typeof e === 'string') ? e : e.toJSON().slice(0,10).split('-').reverse().join('/')).replace(/\//g, ' ') : '';
  }

  toDateChange(e) {
    this.hasChanges = true;
    this.toDateFilter = e ? ((typeof e === 'string') ? e : e.toJSON().slice(0,10).split('-').reverse().join('/')).replace(/\//g, ' ') : '';
  }

  validateDateInput: ValidatorFn = (control: UntypedFormGroup) => {
    const dateFilter = control.get('dateFilter').value;
    let dateOK = false;
    // if date filter set, have to set from and to dates
    if (dateFilter) {
      dateOK = this.fromDateFilter !== '' && this.toDateFilter !== '';
    }
    return dateOK ? null : { dateValidateError: true };
  };

  getBackURL() {
    return this.reportHelper.getBackUrl(this.report);
  }
}
